import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { ApiService } from '../../services/api.service';
import type { AdminRepository } from '../../domain/repositories/admin.repository';
import type {
  Profile,
  Experience,
  Project,
  Technology,
  Education,
  Certification,
  SocialLink,
  SiteConfig,
  SectionConfig,
  GitHubRepo,
} from '../../domain/entities';

/**
 * HTTP adapter for {@link AdminRepository}.
 *
 * Thin layer on top of {@link ApiService}; its sole responsibility is to
 * translate admin domain commands into REST calls against the `/admin` API
 * prefix. No caching, no memoisation — that belongs in facade / view layers.
 */
@Injectable({ providedIn: 'root' })
export class HttpAdminRepository implements AdminRepository {
  private readonly api = inject(ApiService);

  // Profile
  public getProfile(): Observable<Profile> {
    return this.api.get<Profile>('profile');
  }

  public updateProfile(data: Partial<Profile>): Observable<Profile> {
    return this.api.put<Profile>('admin/profile', data);
  }

  // Experiences
  public getExperiences(): Observable<Experience[]> {
    return this.api.get<Experience[]>('experience');
  }

  public createExperience(data: Partial<Experience>): Observable<Experience> {
    return this.api.post<Experience>('admin/experiences', data);
  }

  public updateExperience(id: string, data: Partial<Experience>): Observable<Experience> {
    return this.api.put<Experience>(`admin/experiences/${id}`, data);
  }

  public deleteExperience(id: string): Observable<void> {
    return this.api.delete<void>(`admin/experiences/${id}`);
  }

  // Projects
  public getProjects(): Observable<Project[]> {
    return this.api.get<Project[]>('projects');
  }

  public createProject(data: Partial<Project>): Observable<Project> {
    return this.api.post<Project>('admin/projects', data);
  }

  public updateProject(id: string, data: Partial<Project>): Observable<Project> {
    return this.api.put<Project>(`admin/projects/${id}`, data);
  }

  public deleteProject(id: string): Observable<void> {
    return this.api.delete<void>(`admin/projects/${id}`);
  }

  // Technologies
  public getTechnologies(): Observable<Technology[]> {
    return this.api.get<Technology[]>('technologies/flat');
  }

  public createTechnology(data: Partial<Technology>): Observable<Technology> {
    return this.api.post<Technology>('admin/technologies', data);
  }

  public updateTechnology(id: string, data: Partial<Technology>): Observable<Technology> {
    return this.api.put<Technology>(`admin/technologies/${id}`, data);
  }

  public deleteTechnology(id: string): Observable<void> {
    return this.api.delete<void>(`admin/technologies/${id}`);
  }

  // Education
  public getEducation(): Observable<Education[]> {
    return this.api.get<Education[]>('education');
  }

  public createEducation(data: Partial<Education>): Observable<Education> {
    return this.api.post<Education>('admin/education', data);
  }

  public updateEducation(id: string, data: Partial<Education>): Observable<Education> {
    return this.api.put<Education>(`admin/education/${id}`, data);
  }

  public deleteEducation(id: string): Observable<void> {
    return this.api.delete<void>(`admin/education/${id}`);
  }

  // Certifications
  public getCertifications(): Observable<Certification[]> {
    return this.api.get<Certification[]>('certifications');
  }

  public createCertification(data: Partial<Certification>): Observable<Certification> {
    return this.api.post<Certification>('admin/certifications', data);
  }

  public updateCertification(id: string, data: Partial<Certification>): Observable<Certification> {
    return this.api.put<Certification>(`admin/certifications/${id}`, data);
  }

  public deleteCertification(id: string): Observable<void> {
    return this.api.delete<void>(`admin/certifications/${id}`);
  }

  // Social Links
  public getSocialLinks(): Observable<SocialLink[]> {
    return this.api.get<SocialLink[]>('config/social');
  }

  public createSocialLink(data: Partial<SocialLink>): Observable<SocialLink> {
    return this.api.post<SocialLink>('admin/social-links', data);
  }

  public updateSocialLink(id: string, data: Partial<SocialLink>): Observable<SocialLink> {
    return this.api.put<SocialLink>(`admin/social-links/${id}`, data);
  }

  public deleteSocialLink(id: string): Observable<void> {
    return this.api.delete<void>(`admin/social-links/${id}`);
  }

  // Site Config
  public getSiteConfig(): Observable<SiteConfig> {
    return this.api.get<SiteConfig>('config/site');
  }

  public updateSiteConfig(data: Record<string, string>): Observable<SiteConfig> {
    return this.api.put<SiteConfig>('admin/config/site', { config: data });
  }

  // Sections Config
  public getSections(): Observable<SectionConfig[]> {
    return this.api.get<SectionConfig[]>('config/sections');
  }

  public updateSections(data: SectionConfig[]): Observable<SectionConfig[]> {
    return this.api.put<SectionConfig[]>('admin/config/sections', { sections: data });
  }

  // GitHub
  public getGithubRepos(): Observable<GitHubRepo[]> {
    return this.api.get<GitHubRepo[]>('github/repos');
  }

  public syncGithub(): Observable<{ synced: number }> {
    return this.api.post<{ synced: number }>('admin/github/sync', {});
  }

  public updateGithubRepo(id: string, data: Partial<GitHubRepo>): Observable<GitHubRepo> {
    return this.api.put<GitHubRepo>(`admin/github/repos/${id}`, data);
  }

  // CV Upload
  public uploadCv(file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.postFormData<{ url: string }>('admin/cv/upload', formData);
  }
}
